import {
	Box,
	Checkbox,
	Grid,
	IconButton,
	Menu,
	MenuItem,
	TableCell,
	TableContainer,
	TablePagination,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import SortIcon from '@mui/icons-material/Sort';

import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { grey } from '@mui/material/colors';
import { useCustomTheme } from '../../store/theme-context';
import AuthContext from '../../store/auth-context';
import { useContext } from 'react';

type TableData = {
	columns: {
		header: ReactNode;
		accessor: string;
		width?: string;
		minWidth?: string;
		maxWidth?: string;
		align?: string;
		hidden?: boolean;
		nosort?: boolean;
		bgColor?: string;
		editable?: boolean;
	}[];
	rows: any[];
	tableId?: string;
};

type Props = {
	data: TableData;
	footerLeft?: React.ReactNode;
	/** Domyślne sortowanie (domyślnie: pierwsza kolumna, malejąco). */
	defaultSort?: { accessor: string; desc: boolean };
};
const CustomTable = ({ data, footerLeft, defaultSort }: Props) => {
	const muiTheme = useTheme();
	const { theme, isHighContrast } = useCustomTheme();
	const { id } = useContext(AuthContext);
	const isFooterCompact = useMediaQuery('(max-width:500px)');
	const isDesktop = useMediaQuery('(min-width:900px)');
	const [displayedRows, setDisplayedRows] = useState<any[]>([]);
	const [page, setPage] = useState(0);
	const [perPage, setPerPage] = useState(20);
	const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
	const [shownBaseColumns, setShownBaseColumns] = useState<string[]>([]);
	const [columnOrder, setColumnOrder] = useState<string[]>([]);
	const [columnWidths, setColumnWidths] = useState<Record<string, string>>({});
	const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
	const [menuAnchorPosition, setMenuAnchorPosition] = useState<{ top: number; left: number } | null>(null);
	const clickDelayRef = useRef<number | null>(null);
	const resizingRef = useRef<{
		accessor: string;
		startX: number;
		startWidth: number;
	} | null>(null);
	const resizeBlockClickRef = useRef<boolean>(false);
	const [sortBy, setSortBy] = useState<{ accessor: string; desc: boolean }>(
		defaultSort ?? { accessor: data.columns[0].accessor, desc: true },
	);

	const tableKey = useMemo(() => {
		const accessors = data.columns.map((c) => c.accessor).join('|');
		const tableId = data.tableId ? data.tableId : `${window.location.pathname}:${accessors}`;
		return `tablePrefs:${id ?? 'anon'}:${tableId}`;
	}, [data.columns, data.tableId, id]);

	const baseHiddenAccessors = useMemo(() => {
		return new Set(data.columns.filter((c) => c.hidden).map((c) => c.accessor));
	}, [data.columns]);

	useEffect(() => {
		try {
			const stored = localStorage.getItem(tableKey);
			if (stored) {
				const parsed = JSON.parse(stored);
				if (Array.isArray(parsed.hiddenColumns)) {
					setHiddenColumns(parsed.hiddenColumns);
				}
				if (Array.isArray(parsed.shownBaseColumns)) {
					setShownBaseColumns(parsed.shownBaseColumns);
				}
				if (Array.isArray(parsed.columnOrder)) {
					setColumnOrder(parsed.columnOrder);
				}
				if (parsed.columnWidths && typeof parsed.columnWidths === 'object') {
					setColumnWidths(parsed.columnWidths);
				}
			}
		} catch (error) {
			// Ignore storage errors
		}
	}, [tableKey]);

	useEffect(() => {
		try {
			localStorage.setItem(tableKey, JSON.stringify({ hiddenColumns, shownBaseColumns, columnOrder, columnWidths }));
		} catch (error) {
			// Ignore storage errors
		}
	}, [hiddenColumns, shownBaseColumns, columnOrder, columnWidths, tableKey]);

	useEffect(() => {
		// Clean up hiddenColumns to not include base-hidden accessors (they're handled separately)
		setHiddenColumns((prev) => prev.filter((accessor) => !baseHiddenAccessors.has(accessor)));
		// Clean up shownBaseColumns to only include valid base-hidden accessors
		setShownBaseColumns((prev) => prev.filter((accessor) => baseHiddenAccessors.has(accessor)));
	}, [baseHiddenAccessors]);

	useEffect(() => {
		const baseOrder = data.columns.map((c) => c.accessor);
		setColumnOrder((prev) => {
			if (!prev.length) {
				return baseOrder;
			}
			const filtered = prev.filter((accessor) => baseOrder.includes(accessor));
			const missing = baseOrder.filter((accessor) => !filtered.includes(accessor));
			return [...filtered, ...missing];
		});
	}, [data.columns]);

	const columnsWithVisibility = useMemo(() => {
		const effectiveOrder = columnOrder.length ? columnOrder : data.columns.map((c) => c.accessor);
		const columnsByAccessor = new Map(
			data.columns.map((c) => [
				c.accessor,
				{
					...c,
					width: columnWidths[c.accessor] ?? c.width,
				},
			]),
		);
		const ordered = effectiveOrder
			.map((accessor) => columnsByAccessor.get(accessor))
			.filter(Boolean) as TableData['columns'];
		return ordered.map((c) => {
			const isBaseHidden = baseHiddenAccessors.has(c.accessor);
			let isHidden: boolean;
			if (isBaseHidden) {
				// Base-hidden columns are hidden unless explicitly shown
				isHidden = !shownBaseColumns.includes(c.accessor);
			} else {
				// Regular columns are visible unless user hid them
				isHidden = hiddenColumns.includes(c.accessor);
			}
			return { ...c, hidden: isHidden };
		});
	}, [data.columns, hiddenColumns, shownBaseColumns, baseHiddenAccessors, columnOrder, columnWidths]);

	useEffect(() => {
		const firstVisible = columnsWithVisibility.find((c) => !c.hidden);
		if (sortBy.accessor && columnsWithVisibility.some((c) => c.accessor === sortBy.accessor && c.hidden)) {
			if (firstVisible) {
				setSortBy({ accessor: firstVisible.accessor, desc: sortBy.desc });
			}
		}
	}, [columnsWithVisibility, sortBy.accessor, sortBy.desc]);

	useEffect(() => {
		const getSortValue = (cell: any) => {
			if (!cell || !cell.props) {
				return null;
			}
			if (cell.props['data-sort'] !== undefined) {
				return cell.props['data-sort'];
			}
			if (cell.props.sortValue !== undefined) {
				return cell.props.sortValue;
			}
			return cell.props.children;
		};

		const sortedRows = [
			...data.rows.sort((a: any, b: any) => {
				const aCell = a[sortBy.accessor];
				const bCell = b[sortBy.accessor];
				if (aCell?.props?.checked !== undefined || bCell?.props?.checked !== undefined) {
					const aIsActive = aCell?.props?.checked;
					const bIsActive = bCell?.props?.checked;
					let result = 0;
					if (aIsActive && !bIsActive) {
						result = -1;
					} else if (!aIsActive && bIsActive) {
						result = 1;
					}
					return sortBy.desc ? -result : result;
				}

				const aValue = getSortValue(aCell);
				const bValue = getSortValue(bCell);
				let result = 0;
				if (typeof aValue === 'number' && typeof bValue === 'number') {
					result = aValue - bValue;
				} else if (aValue != null && bValue != null) {
					result = String(aValue).localeCompare(String(bValue), 'pl', { sensitivity: 'base' });
				}
				return sortBy.desc ? -result : result;
			}),
		];
		const startIndex = page * perPage;
		const endIndex = startIndex + perPage;

		setDisplayedRows(sortedRows.slice(startIndex, endIndex));
	}, [sortBy, page, perPage, data]);

	const createHeader = (columns: any[]) => {
		const visibleColumns = columns.filter((c: any) => !c.hidden);
		const blockedAccessors = new Set(['action', 'oralDescription']);
		return (
			<TableHead>
				<TableRow
					sx={{
						'> *:not(:last-child)': {
							borderRight: '1px solid #dddddd',
						},
						borderBottom: `2px solid ${theme.palette.primary.main}`,
					}}
				>
					{columns
						.filter((c) => !c.hidden)
						.map((c) => (
							<TableCell
								key={c.accessor}
								align={c.align}
								draggable={isDesktop && c.header !== '' && c.accessor !== 'action'}
								onDragStart={(event) => {
									if (!isDesktop || c.header === '' || c.accessor === 'action') {
										return;
									}
									event.dataTransfer.setData('text/plain', c.accessor);
									event.dataTransfer.effectAllowed = 'move';
								}}
								onDragOver={(event) => {
									if (!isDesktop) return;
									event.preventDefault();
									event.dataTransfer.dropEffect = 'move';
								}}
								onDrop={(event) => {
									if (!isDesktop) return;
									event.preventDefault();
									const source = event.dataTransfer.getData('text/plain');
									const target = c.accessor;
									if (!source || source === target) return;
									setColumnOrder((prev) => {
										const next = prev.filter((accessor) => accessor !== source);
										const targetIndex = next.indexOf(target);
										if (targetIndex === -1) {
											return prev;
										}
										next.splice(targetIndex, 0, source);
										return next;
									});
								}}
								onClick={(event) => {
									event.preventDefault();
									if (resizeBlockClickRef.current) {
										return;
									}
									if (c.header === '') {
										return;
									}
									if (clickDelayRef.current) {
										window.clearTimeout(clickDelayRef.current);
									}
									const { clientX, clientY } = event;
									const target = event.currentTarget;
									clickDelayRef.current = window.setTimeout(() => {
										setMenuAnchorPosition({ top: clientY, left: clientX });
										setMenuAnchorEl(target);
									}, 180);
								}}
								onDoubleClick={(event) =>
									setHiddenColumns((prev) => {
										event.preventDefault();
										event.stopPropagation();
										if (blockedAccessors.has(c.accessor) || c.header === '') {
											return prev;
										}
										if (clickDelayRef.current) {
											window.clearTimeout(clickDelayRef.current);
											clickDelayRef.current = null;
										}
										if (prev.includes(c.accessor)) {
											return prev;
										}
										if (visibleColumns.length <= 2) {
											return prev;
										}
										return [...prev, c.accessor];
									})
								}
								onContextMenu={(event) => {
									event.preventDefault();
								}}
								sx={{
									width: c.width,
									minWidth: c.minWidth || c.width || '120px',
									maxWidth: c.maxWidth || c.width || '320px',
									backgroundColor: c.editable ? c.bgColor : grey[100],
									color: isHighContrast ? theme.palette.primary.light : theme.palette.primary.main,
									position: 'sticky',
									top: 0,
									zIndex: 1,
								}}
							>
								<Grid
									item
									container
									flexWrap={'nowrap'}
									gap='10px'
									alignItems='center'
								>
									{typeof c.header === 'string' ? (
										<Typography
											sx={{
												fontSize: '16px !important',
												fontWeight: '600',
												color: isHighContrast ? theme.palette.primary.light : theme.palette.primary.main,
												textAlign: c.align ? c.align : 'left',
												whiteSpace: 'nowrap',
												width: '100%',
											}}
										>
											{c.header}
										</Typography>
									) : (
										c.header
									)}
									{!c.nosort && (
										<IconButton
											onClick={(event) => {
												event.stopPropagation();
												setSortBy({ accessor: c.accessor, desc: !sortBy.desc });
											}}
											sx={{
												padding: 0,
												ml: 'auto',
											}}
										>
											{sortBy.accessor === c.accessor ? (
												sortBy.desc ? (
													<ArrowDropDownIcon fontSize={'small'} />
												) : (
													<ArrowDropUpIcon fontSize={'small'} />
												)
											) : (
												<SortIcon fontSize={'small'} />
											)}
										</IconButton>
									)}
								</Grid>
								{isDesktop && c.header !== '' && c.accessor !== 'action' && (
									<span
										onMouseDown={(event) => {
											event.preventDefault();
											event.stopPropagation();
											resizeBlockClickRef.current = true;
											const currentWidth = parseInt((c.width || '150').toString().replace('px', ''), 10) || 150;
											resizingRef.current = {
												accessor: c.accessor,
												startX: event.clientX,
												startWidth: currentWidth,
											};
										}}
										style={{
											position: 'absolute',
											right: 0,
											top: 0,
											width: 8,
											height: '100%',
											cursor: 'col-resize',
										}}
									/>
								)}
							</TableCell>
						))}
				</TableRow>
			</TableHead>
		);
	};

	const printRows = (columns: any[], rows: any[]) => {
		return rows.map((r: any, i: number) => (
			<TableRow
				key={'row-' + i}
				sx={{
					'> *:not(:last-child)': {
						borderRight: '1px solid #dddddd',
					},
				}}
			>
				{columns
					.filter((c) => !c.hidden)
					.map((c: any, i: number) => (
						<TableCell
							key={'row-' + i + c.accessor}
							align={c.align}
							valign={'bottom'}
							sx={{
								width: c.width,
								minWidth: c.minWidth || c.width || '120px',
								maxWidth: c.maxWidth || c.width || '320px',
								backgroundColor: c.editable ? c.bgColor : grey[100],
								whiteSpace: 'normal',
								wordBreak: 'break-word',
								overflowWrap: 'anywhere',
							}}
						>
							{r[c.accessor]}
						</TableCell>
					))}
			</TableRow>
		));
	};

	const renderColumnsMenu = () => {
		const visibleColumns = columnsWithVisibility.filter((c) => !c.hidden);
		const menuColumns = columnsWithVisibility.filter((c) => c.header !== '' && c.accessor !== 'action');
		const toggleHidden = (accessor: string) => {
			const isBaseHidden = baseHiddenAccessors.has(accessor);
			const column = columnsWithVisibility.find((c) => c.accessor === accessor);
			const isCurrentlyHidden = column?.hidden ?? false;

			// Prevent hiding if it would leave fewer than 2 visible columns
			if (!isCurrentlyHidden && visibleColumns.length <= 2) {
				return;
			}

			if (isBaseHidden) {
				// For base-hidden columns: toggle shownBaseColumns
				setShownBaseColumns((prev) =>
					prev.includes(accessor) ? prev.filter((col) => col !== accessor) : [...prev, accessor],
				);
			} else {
				// For regular columns: toggle hiddenColumns
				setHiddenColumns((prev) =>
					prev.includes(accessor) ? prev.filter((col) => col !== accessor) : [...prev, accessor],
				);
			}
		};
		const resetView = () => {
			setHiddenColumns([]);
			setShownBaseColumns([]);
			setColumnOrder(data.columns.map((c) => c.accessor));
			setColumnWidths({});
			setMenuAnchorEl(null);
			setMenuAnchorPosition(null);
		};
		const labelFor = (c: any) => {
			if (c.accessor === 'oralDescription') {
				return 'Charakterystyka';
			}
			return typeof c.header === 'string' && c.header !== '' ? c.header : c.accessor;
		};

		return (
			<Menu
				open={Boolean(menuAnchorEl)}
				anchorEl={menuAnchorEl}
				anchorReference={menuAnchorPosition ? 'anchorPosition' : 'anchorEl'}
				anchorPosition={menuAnchorPosition ?? undefined}
				onClose={() => {
					setMenuAnchorEl(null);
					setMenuAnchorPosition(null);
				}}
			>
				{menuColumns.map((c: any) => (
					<MenuItem
						key={c.accessor}
						onClick={() => toggleHidden(c.accessor)}
					>
						<Checkbox checked={!c.hidden} />
						<Typography variant='body2'>{labelFor(c)}</Typography>
					</MenuItem>
				))}
				<MenuItem onClick={resetView}>
					<Typography variant='body2'>Przywróć oryginalny widok listy</Typography>
				</MenuItem>
			</Menu>
		);
	};

	useEffect(() => {
		const handleMouseMove = (event: MouseEvent) => {
			if (!resizingRef.current) {
				return;
			}
			const { accessor, startX, startWidth } = resizingRef.current;
			const delta = event.clientX - startX;
			const nextWidth = Math.max(80, startWidth + delta);
			setColumnWidths((prev) => ({
				...prev,
				[accessor]: `${nextWidth}px`,
			}));
		};

		const handleMouseUp = () => {
			resizingRef.current = null;
			if (resizeBlockClickRef.current) {
				window.setTimeout(() => {
					resizeBlockClickRef.current = false;
				}, 200);
			}
		};

		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);
		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
		};
	}, []);

	return (
		<>
			<TableContainer
				component={Paper}
				sx={{
					mt: { xs: '16px', sm: '24px', md: '40px' },
					width: '100%',
					maxHeight: {
						xs: 'calc(100vh - var(--panel-header-height, 0px) - var(--panel-stages-tabs-height, 0px) - 180px)',
						sm: 'calc(100vh - var(--panel-header-height, 0px) - var(--panel-stages-tabs-height, 0px) - 170px)',
						md: 'calc(100vh - var(--panel-header-height, 0px) - var(--panel-stages-tabs-height, 0px) - 160px)',
						lg: 'calc(100vh - var(--panel-header-height, 0px) - var(--panel-stages-tabs-height, 0px) - 150px)',
						xl: 'calc(100vh - var(--panel-header-height, 0px) - var(--panel-stages-tabs-height, 0px) - 140px)',
					},
					minHeight: { xs: '220px', sm: '260px', md: '300px' },
					overflowX: 'auto',
					overflowY: 'auto',
					scrollbarGutter: 'stable both-edges',
				}}
			>
				<Table
					stickyHeader
					sx={{ width: 'max-content', minWidth: '100%', tableLayout: 'auto' }}
				>
					{createHeader(columnsWithVisibility)}
					<TableBody>{printRows(columnsWithVisibility, displayedRows)}</TableBody>
				</Table>
				{renderColumnsMenu()}
			</TableContainer>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					backgroundColor: theme.palette.primary.main,
					color: muiTheme.palette.common.white,
				}}
			>
				{footerLeft && (
					<Box sx={{ display: 'flex', alignItems: 'center', pl: isFooterCompact ? 1 : 2, gap: 1 }}>{footerLeft}</Box>
				)}
				<TablePagination
					rowsPerPageOptions={[20, 50, 100]}
					component='div'
					count={data.rows.length}
					rowsPerPage={perPage}
					page={page}
					onPageChange={(event, page) => setPage(page)}
					labelRowsPerPage={isFooterCompact ? '' : 'Wierszy na stronie'}
					labelDisplayedRows={({ from, to, count }) => `${from}-${to}  Łącznie: ${count}`}
					SelectProps={{
						'aria-label': 'Wierszy na stronie',
						title: 'Wierszy na stronie',
					}}
					onRowsPerPageChange={(event) => {
						setPage(0);
						setPerPage(Number(event.target.value));
					}}
					sx={{
						flex: 1,
						color: theme.palette.primary.light,
						backgroundColor: 'transparent',
						'.MuiTablePagination-toolbar': {
							px: isFooterCompact ? 1 : 2,
							flexWrap: isFooterCompact ? 'wrap' : 'nowrap',
							gap: isFooterCompact ? 1 : 0,
						},
						'.MuiTablePagination-selectLabel': {
							display: isFooterCompact ? 'none' : 'block',
						},
						'.MuiTablePagination-select': {
							color: muiTheme.palette.common.white,
						},
						'.MuiTablePagination-selectIcon': {
							color: muiTheme.palette.common.white,
						},
						'.MuiTablePagination-actions button': {
							color: muiTheme.palette.common.white,
						},
						'.MuiTablePagination-actions svg': {
							color: muiTheme.palette.common.white,
						},
					}}
				/>
			</Box>
		</>
	);
};

export default CustomTable;
